import { useState } from 'react';
import { FiMaximize2, FiMinimize2 } from 'react-icons/fi';
import MetricsTable from './MetricsTable';
import EquityCurveLog from './EquityCurveLog';
import UnderwaterPlot from './UnderwaterPlot';
import RollingStatsChart from './RollingStatsChart';
import MonthlyHeatmap from './MonthlyHeatmap';
import ForensicStats from './ForensicStats';
import ReturnDistribution from './ReturnDistribution';
import DrawdownTable from './DrawdownTable';

export interface TearSheetData {
  basic: {
    total_return: number;
    cagr: number;
    sharpe_ratio: number;
    sortino_ratio: number;
    calmar_ratio: number;
    max_drawdown: number;
    win_rate: number;
    profit_factor: number;
  };
  advanced: {
    deflated_sharpe_ratio: number;
    information_ratio: number;
    omega_ratio: number;
    tail_ratio: number;
    treynor_ratio: number;
    beta: number;
    alpha: number;
  };
  tail_risk: {
    skewness: number;
    kurtosis: number;
    var_95: number;
    cvar_95: number;
  };
  rolling: {
    sharpe: number[];
    sortino: number[];
    volatility: number[];
    beta: number[];
  };
  drawdown: {
    underwater: number[];
    max_duration_days: number;
    recovery_factor: number;
  };
  monthly_returns: Record<number, Record<number, number>>;
  metadata: {
    start_date: string;
    end_date: string;
    trading_days: number;
    trial_count: number;
  };
  /** Daily returns for equity curve */
  returns?: number[];
  /** Benchmark returns for comparison */
  benchmark_returns?: number[];
}

interface TearSheetProps {
  data: TearSheetData;
  /** Strategy name for header */
  strategyName?: string;
  /** Show advanced view by default */
  defaultAdvanced?: boolean;
  /** Compact mode for smaller spaces */
  compact?: boolean;
  /** Forensic mode — renders additional deep analytics sections */
  forensic?: boolean;
}

/**
 * Complete forensic tear sheet component.
 *
 * Displays institutional-grade performance metrics including:
 * - Log-scale equity curve
 * - Metrics table (basic + advanced)
 * - Rolling statistics (6-month)
 * - Underwater (drawdown) plot
 * - Monthly returns heatmap
 */
export default function TearSheet({
  data,
  strategyName,
  defaultAdvanced = false,
  compact = false,
  forensic = false
}: TearSheetProps) {
  const [showAdvanced, setShowAdvanced] = useState(defaultAdvanced);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const toggleExpand = (section: string) => {
    setExpandedSection(prev => prev === section ? null : section);
  };

  // Full-width expanded view
  if (expandedSection) {
    return (
      <div className="fixed inset-0 bg-black/90 z-50 p-8 overflow-auto">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-white">
              {strategyName && `${strategyName} - `}
              {expandedSection === 'equity' && 'Equity Curve'}
              {expandedSection === 'underwater' && 'Underwater Plot'}
              {expandedSection === 'rolling' && 'Rolling Statistics'}
              {expandedSection === 'heatmap' && 'Monthly Returns'}
              {expandedSection === 'distribution' && 'Return Distribution'}
              {expandedSection === 'drawdowns' && 'Top Drawdowns'}
            </h2>
            <button
              onClick={() => setExpandedSection(null)}
              className="p-2 text-gray-400 hover:text-white transition-colors"
            >
              <FiMinimize2 className="w-5 h-5" />
            </button>
          </div>

          {expandedSection === 'equity' && data.returns && (
            <EquityCurveLog
              returns={data.returns}
              startDate={data.metadata.start_date}
              benchmarkReturns={data.benchmark_returns}
              height={600}
            />
          )}
          {expandedSection === 'underwater' && (
            <UnderwaterPlot
              underwater={data.drawdown.underwater}
              startDate={data.metadata.start_date}
              maxDurationDays={data.drawdown.max_duration_days}
              height={400}
            />
          )}
          {expandedSection === 'rolling' && (
            <RollingStatsChart
              rollingSharpe={data.rolling.sharpe}
              rollingSortino={data.rolling.sortino}
              rollingVolatility={data.rolling.volatility}
              rollingBeta={data.rolling.beta}
              startDate={data.metadata.start_date}
              height={500}
            />
          )}
          {expandedSection === 'heatmap' && (
            <MonthlyHeatmap monthlyReturns={data.monthly_returns} />
          )}
          {expandedSection === 'distribution' && data.returns && (
            <ReturnDistribution returns={data.returns} height={500} />
          )}
          {expandedSection === 'drawdowns' && data.returns && (
            <DrawdownTable returns={data.returns} startDate={data.metadata.start_date} />
          )}
        </div>
      </div>
    );
  }

  // Compact layout for smaller spaces
  if (compact) {
    return (
      <div className="space-y-4">
        <MetricsTable
          metrics={{
            basic: data.basic,
            advanced: data.advanced,
            tail_risk: data.tail_risk,
            metadata: data.metadata
          }}
          showAdvanced={showAdvanced}
        />

        {data.returns && (
          <EquityCurveLog
            returns={data.returns}
            startDate={data.metadata.start_date}
            benchmarkReturns={data.benchmark_returns}
            height={200}
          />
        )}
      </div>
    );
  }

  // Full tear sheet layout
  return (
    <div className="space-y-6">
      {/* Header */}
      {strategyName && (
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-white">{strategyName}</h2>
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className={`px-3 py-1 text-sm rounded-full transition-colors ${
              showAdvanced
                ? 'bg-profit/20 text-profit'
                : 'bg-gray-800 text-gray-400 hover:text-white'
            }`}
          >
            {showAdvanced ? 'Advanced View' : 'Basic View'}
          </button>
        </div>
      )}

      {/* Top row: Equity curve */}
      <div className="relative">
        {data.returns && (
          <>
            <EquityCurveLog
              returns={data.returns}
              startDate={data.metadata.start_date}
              benchmarkReturns={data.benchmark_returns}
            />
            <button
              onClick={() => toggleExpand('equity')}
              className="absolute top-4 right-4 p-1.5 bg-gray-800/80 rounded text-gray-400 hover:text-white transition-colors"
            >
              <FiMaximize2 className="w-4 h-4" />
            </button>
          </>
        )}
      </div>

      {/* Middle row: Metrics + Rolling/Underwater */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <MetricsTable
          metrics={{
            basic: data.basic,
            advanced: data.advanced,
            tail_risk: data.tail_risk,
            metadata: data.metadata
          }}
          showAdvanced={showAdvanced}
        />

        <div className="space-y-6">
          {/* Rolling Stats */}
          {(data.rolling.sharpe.length > 0 || data.rolling.sortino.length > 0) && (
            <div className="relative">
              <RollingStatsChart
                rollingSharpe={data.rolling.sharpe}
                rollingSortino={data.rolling.sortino}
                rollingVolatility={data.rolling.volatility}
                rollingBeta={data.rolling.beta}
                startDate={data.metadata.start_date}
                height={200}
              />
              <button
                onClick={() => toggleExpand('rolling')}
                className="absolute top-4 right-4 p-1.5 bg-gray-800/80 rounded text-gray-400 hover:text-white transition-colors"
              >
                <FiMaximize2 className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Underwater Plot */}
          {data.drawdown.underwater.length > 0 && (
            <div className="relative">
              <UnderwaterPlot
                underwater={data.drawdown.underwater}
                startDate={data.metadata.start_date}
                maxDurationDays={data.drawdown.max_duration_days}
                height={150}
              />
              <button
                onClick={() => toggleExpand('underwater')}
                className="absolute top-4 right-4 p-1.5 bg-gray-800/80 rounded text-gray-400 hover:text-white transition-colors"
              >
                <FiMaximize2 className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Bottom row: Monthly heatmap */}
      {Object.keys(data.monthly_returns).length > 0 && (
        <div className="relative">
          <MonthlyHeatmap monthlyReturns={data.monthly_returns} />
          <button
            onClick={() => toggleExpand('heatmap')}
            className="absolute top-4 right-4 p-1.5 bg-gray-800/80 rounded text-gray-400 hover:text-white transition-colors"
          >
            <FiMaximize2 className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Forensic-only sections */}
      {forensic && data.returns && data.returns.length > 1 && (
        <>
          <ForensicStats
            returns={data.returns}
            benchmarkReturns={data.benchmark_returns}
          />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="relative">
              <ReturnDistribution returns={data.returns} />
              <button
                onClick={() => toggleExpand('distribution')}
                className="absolute top-4 right-4 p-1.5 bg-gray-800/80 rounded text-gray-400 hover:text-white transition-colors"
              >
                <FiMaximize2 className="w-4 h-4" />
              </button>
            </div>
            <div className="relative">
              <DrawdownTable returns={data.returns} startDate={data.metadata.start_date} />
              <button
                onClick={() => toggleExpand('drawdowns')}
                className="absolute top-4 right-4 p-1.5 bg-gray-800/80 rounded text-gray-400 hover:text-white transition-colors"
              >
                <FiMaximize2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </>
      )}

      {/* DSR Warning if trial count is high */}
      {data.metadata.trial_count > 10 && data.advanced.deflated_sharpe_ratio < 0.5 && (
        <div className="p-4 bg-amber-900/20 border border-amber-800/50 rounded-md">
          <p className="text-amber-400 text-sm">
            <strong>Note:</strong> Deflated Sharpe Ratio ({data.advanced.deflated_sharpe_ratio.toFixed(2)}) is low
            relative to {data.metadata.trial_count} backtests run. This may indicate overfitting.
          </p>
        </div>
      )}
    </div>
  );
}

// Re-export sub-components for granular use
export { MetricsTable, EquityCurveLog, UnderwaterPlot, RollingStatsChart, MonthlyHeatmap, ForensicStats, ReturnDistribution, DrawdownTable };
